import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';

const BusinessQuoteForm = () => {
  const routeBase = useWorkspaceBasePath();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  
  // État pour l'autocomplétion des produits
  const [activeProductDropdown, setActiveProductDropdown] = useState(null);
  const [productSearches, setProductSearches] = useState({});
  const dropdownRefs = useRef({});
  
  const [form, setForm] = useState({
    client_id: '',
    date_devis: new Date().toISOString().split('T')[0],
    date_validite: '',
    notes: '',
    conditions: ''
  });
  
  const [items, setItems] = useState([
    { description: '', quantite: 1, prix_unitaire: '', product_id: '' }
  ]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  
  // Fermer le dropdown quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeProductDropdown !== null) {
        const ref = dropdownRefs.current[activeProductDropdown];
        if (ref && !ref.contains(event.target)) {
          setActiveProductDropdown(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeProductDropdown]);

  const loadData = async () => {
    setLoading(true);
    try {
      const clientsRes = await api.get('/business/clients');
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : []);
      
      // Charger les produits
      const productsRes = await api.get('/business/products');
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);

      if (id) {
        const res = await api.get(`/business/quotes/${id}`);
        if (res.data) {
          setForm({
            client_id: res.data.client_id || '',
            date_devis: res.data.date_devis?.split(' ')[0] || new Date().toISOString().split('T')[0],
            date_validite: res.data.date_validite?.split(' ')[0] || '',
            notes: res.data.notes || '',
            conditions: res.data.conditions || ''
          });
          if (res.data.items && res.data.items.length > 0) {
            setItems(res.data.items.map(item => ({
              id: item.id,
              description: item.description || '',
              quantite: item.quantite || 1,
              prix_unitaire: item.prix_unitaire || '',
              product_id: item.product_id || ''
            })));
          }
        }
      }

      // Set default validity date (30 days from now)
      if (!id) {
        const validityDate = new Date();
        validityDate.setDate(validityDate.getDate() + 30);
        setForm(f => ({ ...f, date_validite: validityDate.toISOString().split('T')[0] }));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = () => {
    setItems([...items, { description: '', quantite: 1, prix_unitaire: '', product_id: '' }]);
  };

  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
      // Nettoyer les états de recherche
      const newSearches = { ...productSearches };
      delete newSearches[index];
      setProductSearches(newSearches);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  // Fonctions pour l'autocomplétion des produits
  const handleProductSelect = (index, product) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      product_id: product.id,
      description: product.nom,
      prix_unitaire: product.prix_vente || product.prix || ''
    };
    setItems(newItems);
    setActiveProductDropdown(null);
    setProductSearches({ ...productSearches, [index]: '' });
  };

  const handleProductSearchChange = (index, value) => {
    setProductSearches({ ...productSearches, [index]: value });
    handleItemChange(index, 'description', value);
    setActiveProductDropdown(index);
  };

  const getFilteredProducts = (index) => {
    const search = (productSearches[index] || '').toLowerCase();
    if (!search) return products;
    return products.filter(p => 
      p.nom?.toLowerCase().includes(search) ||
      p.reference?.toLowerCase().includes(search)
    );
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => {
      return sum + (parseFloat(item.quantite) || 0) * (parseFloat(item.prix_unitaire) || 0);
    }, 0);
  };

  const handleSubmit = async (e, action = 'save') => {
    e.preventDefault();
    
    if (!form.client_id) {
      alert('Veuillez sélectionner un client');
      return;
    }
    
    const validItems = items.filter(item => item.description && item.prix_unitaire);
    if (validItems.length === 0) {
      alert('Ajoutez au moins une ligne');
      return;
    }

    // Calculer le total de chaque item et les totaux globaux
    const itemsWithTotal = validItems.map(item => ({
      ...item,
      quantite: parseFloat(item.quantite) || 1,
      prix_unitaire: parseFloat(item.prix_unitaire) || 0,
      total: (parseFloat(item.quantite) || 1) * (parseFloat(item.prix_unitaire) || 0)
    }));
    
    const sousTotal = itemsWithTotal.reduce((sum, item) => sum + item.total, 0);

    setSaving(true);
    try {
      const data = {
        ...form,
        items: itemsWithTotal,
        sous_total: sousTotal,
        total: sousTotal,
        statut: action === 'send' ? 'envoye' : 'brouillon'
      };

      if (id) {
        await api.put(`/business/quotes/${id}`, data);
      } else {
        await api.post('/business/quotes', data);
      }
      
      navigate(`${routeBase}/quotes`);
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const subtotal = calculateSubtotal();
  const selectedClient = clients.find(c => String(c.id) === String(form.client_id));

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link to={`${routeBase}/quotes`} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200">
          ← Retour
        </Link>
        <h1 className="text-2xl font-bold text-gray-800">
          {id ? 'Modifier le devis' : 'Nouveau devis'}
        </h1>
      </div>

      <form onSubmit={(e) => handleSubmit(e, 'save')}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Client Selection */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="font-semibold text-gray-800 mb-4">Client</h2>
              
              <select
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="">Sélectionner un client</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.nom}
                  </option>
                ))}
              </select>
              
              {selectedClient && (
                <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                  <div className="text-sm text-gray-600">
                    <div className="font-medium text-gray-800">{selectedClient.nom}</div>
                    {selectedClient.telephone && <div>📱 {selectedClient.telephone}</div>}
                    {selectedClient.email && <div>📧 {selectedClient.email}</div>}
                  </div>
                </div>
              )}
            </div>

            {/* Quote Details */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="font-semibold text-gray-800 mb-4">Détails devis</h2>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date du devis</label>
                  <input
                    type="date"
                    value={form.date_devis}
                    onChange={(e) => setForm({ ...form, date_devis: e.target.value })}
                    className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de validité</label>
                  <input
                    type="date"
                    value={form.date_validite}
                    onChange={(e) => setForm({ ...form, date_validite: e.target.value })}
                    className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            {/* Quote Items */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="font-semibold text-gray-800 mb-4">Articles / Services</h2>
              
              <div className="space-y-4">
                {items.map((item, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-xl">
                    <div className="grid grid-cols-12 gap-3">
                      <div className="col-span-12 md:col-span-6">
                        <label className="block text-xs text-gray-500 mb-1">Produit/Service *</label>
                        <div className="relative" ref={el => dropdownRefs.current[index] = el}>
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => handleProductSearchChange(index, e.target.value)}
                            onFocus={() => setActiveProductDropdown(index)}
                            placeholder="Rechercher ou saisir un produit..."
                            className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                            required
                          />
                          {activeProductDropdown === index && products.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                              {getFilteredProducts(index).slice(0, 10).map(product => (
                                <div
                                  key={product.id}
                                  onClick={() => handleProductSelect(index, product)}
                                  className="px-3 py-2 hover:bg-indigo-50 cursor-pointer text-sm border-b last:border-b-0"
                                >
                                  <div className="font-medium">{product.nom}</div>
                                  <div className="text-xs text-gray-500 flex justify-between">
                                    <span>{product.reference || 'Sans référence'}</span>
                                    <span className="font-medium text-indigo-600">{product.prix_vente || product.prix || 0} XOF</span>
                                  </div>
                                </div>
                              ))}
                              {getFilteredProducts(index).length === 0 && (
                                <div className="px-3 py-2 text-sm text-gray-500">Aucun produit trouvé</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-span-4 md:col-span-2">
                        <label className="block text-xs text-gray-500 mb-1">Quantité</label>
                        <input
                          type="number"
                          min="1"
                          step="0.01"
                          value={item.quantite}
                          onChange={(e) => handleItemChange(index, 'quantite', e.target.value)}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 text-center"
                          required
                        />
                      </div>
                      <div className="col-span-5 md:col-span-3">
                        <label className="block text-xs text-gray-500 mb-1">Prix unitaire</label>
                        <input
                          type="number"
                          min="0"
                          value={item.prix_unitaire}
                          onChange={(e) => handleItemChange(index, 'prix_unitaire', e.target.value)}
                          placeholder="0"
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                          required
                        />
                      </div>
                      <div className="col-span-3 md:col-span-1 flex items-end">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          disabled={items.length === 1}
                          className="w-full py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    <div className="text-right mt-2 text-sm font-medium text-gray-600">
                      Total ligne: {((parseFloat(item.quantite) || 0) * (parseFloat(item.prix_unitaire) || 0)).toLocaleString()} XOF
                    </div>
                  </div>
                ))}
              </div>
              
              <button
                type="button"
                onClick={handleAddItem}
                className="mt-4 px-4 py-2 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-indigo-500 hover:text-indigo-600 transition w-full"
              >
                + Ajouter une ligne
              </button>
            </div>

            {/* Notes */}
            <div className="bg-white rounded-xl shadow p-6">
              <h2 className="font-semibold text-gray-800 mb-4">Notes & Conditions</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows="2"
                    className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                    placeholder="Notes ou remarques..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Conditions</label>
                  <textarea
                    value={form.conditions}
                    onChange={(e) => setForm({ ...form, conditions: e.target.value })}
                    rows="2"
                    className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
                    placeholder="Conditions particulières..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow p-6 sticky top-6">
              <h2 className="font-semibold text-gray-800 mb-4">Récapitulatif</h2>
              
              <div className="space-y-3">
                <div className="flex justify-between text-gray-600">
                  <span>Articles ({items.filter(i => i.description).length})</span>
                  <span>{subtotal.toLocaleString()} XOF</span>
                </div>
                
                <div className="border-t pt-3">
                  <div className="flex justify-between text-lg font-bold text-gray-800">
                    <span>Total</span>
                    <span className="text-indigo-600">{subtotal.toLocaleString()} XOF</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 space-y-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition disabled:opacity-50"
                >
                  {saving ? 'Enregistrement...' : '💾 Enregistrer brouillon'}
                </button>
                
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, 'send')}
                  disabled={saving}
                  className="w-full py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition disabled:opacity-50"
                >
                  {saving ? 'Envoi...' : '📤 Enregistrer & Envoyer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BusinessQuoteForm;
